/**
 * Admin — manage discount codes.
 * Codes gate self-service registration in the MVP (100% off code = free access).
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, ArrowLeft, Plus, Trash2, Link2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_TOKEN_KEY = "hp_admin_token";

const adminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}` });

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "-";
  }
};

export const AdminDiscountCodes = () => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ code: "", discount_percent: "100", expires_at: "", max_uses: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/discount-codes`, { headers: adminHeaders() });
      setCodes(res.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        navigate("/sportly-admin");
        return;
      }
      toast.error("Kortingscodes laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem(ADMIN_TOKEN_KEY)) {
      navigate("/sportly-admin");
      return;
    }
    load();
  }, [load, navigate]);

  const openCreate = () => {
    setForm({ code: "", discount_percent: "100", expires_at: "", max_uses: "" });
    setCreateOpen(true);
  };

  const submitCreate = async (ev) => {
    ev.preventDefault();
    if (!form.code.trim()) { toast.error("Code is verplicht"); return; }
    const percent = parseInt(form.discount_percent, 10);
    if (isNaN(percent) || percent < 0 || percent > 100) {
      toast.error("Kortingspercentage moet 0-100 zijn"); return;
    }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_percent: percent,
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      };
      await axios.post(`${API}/admin/discount-codes`, payload, { headers: adminHeaders() });
      toast.success("Kortingscode aangemaakt");
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${deleteTarget.id}`, { headers: adminHeaders() });
      toast.success("Kortingscode verwijderd");
      setCodes((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    } catch {
      toast.error("Verwijderen mislukt");
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/sportly-admin");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-discount-codes-page">
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/sportly-admin/dashboard")}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Terug
          </Button>
          <h1 className="text-lg font-semibold flex-1">Kortingscodes</h1>
          <Button onClick={openCreate} size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="admin-new-code-btn">
            <Plus className="w-4 h-4 mr-1" /> Nieuwe code
          </Button>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-slate-800">
            <LogOut className="w-4 h-4 mr-1.5" /> Uitloggen
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <div className="rounded-lg border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="discount-codes-table">
              <thead>
                <tr className="bg-slate-900/60 border-b border-slate-800">
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Code</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Korting</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Verloopt</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Gebruikt</th>
                  <th className="text-left px-3 py-2.5 font-medium text-slate-400">Aangemaakt</th>
                  <th className="text-right px-3 py-2.5 font-medium text-slate-400">Actie</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-500">Laden...</td></tr>
                ) : codes.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-500" data-testid="discount-codes-empty">Nog geen kortingscodes. Maak je eerste aan met "Nieuwe code".</td></tr>
                ) : codes.map((c) => (
                  <tr key={c.id} className="border-b border-slate-800/50 hover:bg-slate-900/30" data-testid={`discount-code-row-${c.id}`}>
                    <td className="px-3 py-2.5 font-mono font-medium text-amber-300">{c.code}</td>
                    <td className="px-3 py-2.5">{c.discount_percent}%</td>
                    <td className="px-3 py-2.5 text-slate-400">{formatDate(c.expires_at)}</td>
                    <td className="px-3 py-2.5">
                      {c.used_count}
                      {c.max_uses !== null && c.max_uses !== undefined ? ` / ${c.max_uses}` : ""}
                    </td>
                    <td className="px-3 py-2.5 text-slate-400">{formatDate(c.created_at)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const link = `${window.location.origin}/abonnementen?code=${encodeURIComponent(c.code)}`;
                          navigator.clipboard.writeText(link)
                            .then(() => toast.success("Aanmeldlink gekopieerd"))
                            .catch(() => toast.error("Kopiëren mislukt"));
                        }}
                        title="Aanmeldlink kopiëren"
                        data-testid={`copy-link-${c.id}`}
                        className="text-slate-300 hover:text-amber-300"
                      >
                        <Link2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(c)} data-testid={`delete-code-${c.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-400" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-discount-code-dialog">
          <DialogHeader>
            <DialogTitle>Nieuwe kortingscode</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div>
              <Label htmlFor="new-code">Code</Label>
              <Input
                id="new-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="Bijv. TESTGROEP2026"
                data-testid="new-code-input"
              />
            </div>
            <div>
              <Label htmlFor="new-percent">Kortingspercentage</Label>
              <Input
                id="new-percent"
                type="number"
                min={0}
                max={100}
                value={form.discount_percent}
                onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))}
                data-testid="new-percent-input"
              />
            </div>
            <div>
              <Label htmlFor="new-expires">Verloopt op <span className="text-slate-500 font-normal">(optioneel)</span></Label>
              <Input
                id="new-expires"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))}
                data-testid="new-expires-input"
              />
            </div>
            <div>
              <Label htmlFor="new-max">Maximaal aantal gebruik <span className="text-slate-500 font-normal">(optioneel)</span></Label>
              <Input
                id="new-max"
                type="number"
                min={1}
                value={form.max_uses}
                onChange={(e) => setForm((f) => ({ ...f, max_uses: e.target.value }))}
                placeholder="Leeg = onbeperkt"
                data-testid="new-max-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuleren</Button>
              <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="submit-new-code-btn">
                {saving ? "Opslaan..." : "Aanmaken"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kortingscode verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Deze actie kan niet ongedaan worden gemaakt. Nieuwe registraties kunnen deze code niet meer gebruiken.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-code">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDiscountCodes;
